"use client"

import type React from "react"
import { Building2, TrendingUp } from "lucide-react"

interface DataItem {
  department: string
  averageScore: number
}

interface AverageScoresByDepartmentProps {
  data: DataItem[]
}

const AverageScoresByDepartment: React.FC<AverageScoresByDepartmentProps> = ({ data }) => {
  const maxScore = Math.max(...data.map((item) => item.averageScore), 5)
  const colors = [
    "#3B82F6", // Blue
    "#8B5CF6", // Purple
    "#10B981", // Emerald
    "#F59E0B", // Amber
    "#EF4444", // Red
    "#06B6D4", // Cyan
    "#84CC16", // Lime
    "#F97316", // Orange
  ]

  if (data.length === 0) {
    return (
      <div className="w-full">
        <div className="flex items-center mb-6">
          <Building2 className="h-6 w-6 text-blue-600 mr-3" />
          <h2 className="text-xl font-bold text-gray-900">Average Scores by Department</h2>
        </div>
        <div className="text-center py-8">
          <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No department data available</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      <div className="flex items-center mb-6">
        <Building2 className="h-6 w-6 text-blue-600 mr-3" />
        <h2 className="text-xl font-bold text-gray-900">Average Scores by Department</h2>
      </div>

      <div className="space-y-4">
        {data.map((item, index) => {
          const percentage = (item.averageScore / maxScore) * 100
          const color = colors[index % colors.length]

          return (
            <div key={item.department} className="group">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 truncate">{item.department}</span>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-gray-900">{item.averageScore.toFixed(1)}</span>
                  <TrendingUp className="h-4 w-4 text-green-500" />
                </div>
              </div>

              <div className="relative">
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-1000 ease-out relative"
                    style={{
                      width: `${Math.min(percentage, 100)}%`,
                      backgroundColor: color,
                    }}
                  >
                    <div className="absolute inset-0 bg-white/20 animate-pulse" />
                  </div>
                </div>

                {/* Score indicator */}
                <div
                  className="absolute top-0 transform -translate-x-1/2 -translate-y-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                  style={{ left: `${Math.min(percentage, 100)}%` }}
                >
                  <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg">
                    {item.averageScore.toFixed(2)}
                  </div>
                  <div className="w-2 h-2 bg-gray-900 transform rotate-45 mx-auto -mt-1" />
                </div>
              </div>

              {/* Department stats */}
              <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                <span>Department Average</span>
                <span>{percentage.toFixed(1)}% of max score</span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Summary stats */}
      <div className="mt-6 grid grid-cols-2 gap-4">
        <div className="bg-blue-50 rounded-lg p-3">
          <p className="text-xs text-blue-600 font-medium">Highest Score</p>
          <p className="text-lg font-bold text-blue-900">
            {Math.max(...data.map((item) => item.averageScore)).toFixed(1)}
          </p>
        </div>
        <div className="bg-green-50 rounded-lg p-3">
          <p className="text-xs text-green-600 font-medium">Average Score</p>
          <p className="text-lg font-bold text-green-900">
            {(data.reduce((sum, item) => sum + item.averageScore, 0) / data.length).toFixed(1)}
          </p>
        </div>
      </div>
    </div>
  )
}

export default AverageScoresByDepartment
